import React, { useState, useEffect, Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ref, onValue, update, remove, set } from "firebase/database";
import { v4 as uuidv4 } from "uuid";
import { DragDropProvider } from "@dnd-kit/react";
import { isSortableOperation, useSortable } from "@dnd-kit/react/sortable";
import {
  Autocomplete,
  CssBaseline,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Grid,
  OutlinedInput,
  InputAdornment,
  IconButton,
  useTheme,
  Stack,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PersonIcon from "@mui/icons-material/Person";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteIcon from "@mui/icons-material/Delete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Formik } from "formik";
import * as yup from "yup";

import { db } from "../../../../../../firebase";
import Topbar from "../../../../../../components/topbar/TopBar";
import Header from "../../../../../../components/header/Header";
import { tokens } from "../../../../../../theme";
import SnackBar from "../../../../../../components/snackbar/SnackBar";
import { getDriverAndVehicle } from "../../../../../../api/users/driver/driver";
import {
  getTokenRefresh,
  verifyExpireTokenRefresh,
} from "../../../../../../helpers/token/refreshToken";
import { getEnterpriseEmployeeAddress } from "../../../../../../api/enterprise/address";
import {
  createBookingBusiness,
  updateBookingBusiness,
} from "../../../../../../api/booking/bookingBusiness";
import {
  LocalizationProvider,
  MobileDatePicker,
  MobileTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { dateFormat, timeFormat } from "../../../../../../helpers/date/date";
import { formatterTimePicker } from "../../../../../../helpers/date/formatterTimePicker";
import { createBookingCopy } from "../../../../../../helpers/booking/createBookingCopy";
import { sendBookingDriverNotificationUseCase } from "../../../../../../features/booking/driverNotification/domain/usecases/sendBookingDriverNotificationUseCase";
import { UpdateOrderBackup } from "../../../backup/usecases/UpdateOrderBackup";
import { DuplicateOrderBackup } from "../../../backup/usecases/DuplicateOrderBackup";
import { CreateReturnOrderBackup } from "../../../backup/usecases/CreateReturnOrderBackup";
import {
  createAdvanceCash,
  getAdvanceCashOrder,
} from "../../../../../../api/booking/advance";
import { EndDateTimeDialog } from "../../components/EndDateTimeDialog";

import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import FileCopyIcon from "@mui/icons-material/FileCopy";

function normalizeSubRoute(subRoute) {
  if (!subRoute) return null;

  return {
    id: subRoute.id,
    name: subRoute.name,
    lat: subRoute.lat ?? subRoute.latitude,
    lng: subRoute.lng ?? subRoute.longitude,
    url: subRoute.url ?? "",
  };
}

function getOrderedSubRoutesList(booking) {
  const subRoutes = booking?.subRoutes ? Object.values(booking.subRoutes) : [];
  const destination = normalizeSubRoute(booking?.destination);
  const orderedSubRoutes = subRoutes
    .map(normalizeSubRoute)
    .filter((subRoute) => subRoute?.name)
    .filter(
      (subRoute) =>
        subRoute.name !== destination?.name ||
        subRoute.lat !== destination?.lat ||
        subRoute.lng !== destination?.lng,
    )
    .sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0));

  if (destination?.name) {
    orderedSubRoutes.push(destination);
  }

  return orderedSubRoutes;
}

function getDestinationValue(subRoutesList) {
  if (!Array.isArray(subRoutesList) || subRoutesList.length === 0) return "";
  if (subRoutesList.length === 1) return subRoutesList[0].name || "";

  return `${subRoutesList.length} paraderos`;
}

function arrayMove(items, fromIndex, toIndex) {
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);

  return nextItems;
}

function getSortableSubRoutes(subRoutesList) {
  return subRoutesList.map((subRoute, index) => ({
    ...subRoute,
    sortableId: `booking-business-later-sub-route-${subRoute.id || "destination"}-${index}`,
  }));
}

function getSubRoutesFromModalList(subRoutesList) {
  return subRoutesList.map(({ sortableId, ...subRoute }) => subRoute);
}

function getRouteDestination(route) {
  if (!route) return null;

  const { sortableId, id, ...destination } = route;
  return destination;
}

function isSameRoute(routeA, routeB) {
  return (
    routeA?.name === routeB?.name &&
    routeA?.lat === routeB?.lat &&
    routeA?.lng === routeB?.lng
  );
}

function getBookingRouteValues(subRoutesList) {
  const routes = Array.isArray(subRoutesList)
    ? subRoutesList
        .map((route) => {
          if (!route) return null;

          const { sortableId, ...subRoute } = route;
          return subRoute;
        })
        .filter((route) => route?.name)
    : [];

  if (routes.length === 0) {
    return {
      destination: null,
      subRoutes: [],
    };
  }

  const destination = routes[routes.length - 1];
  const subRoutes = routes
    .slice(0, -1)
    .filter((route) => !isSameRoute(route, destination))
    .map((route, index) => ({
      ...route,
      id: index + 1,
    }));

  return {
    destination: getRouteDestination(destination),
    subRoutes,
  };
}

function getNextSubRouteId(subRoutesList) {
  const ids = subRoutesList.map((subRoute) => Number(subRoute.id || 0));
  return Math.max(0, ...ids) + 1;
}

function getDateOnlyValue(value) {
  const dateValue = value?.$d || value;
  if (!dateValue) return null;

  if (dateValue instanceof Date) {
    return new Date(
      dateValue.getFullYear(),
      dateValue.getMonth(),
      dateValue.getDate(),
    );
  }

  if (typeof dateValue === "string") {
    const [day, month, year] = dateValue.split("/");
    if (!day || !month || !year) return null;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  return null;
}

function getTimeMinutesValue(value) {
  const timeValue = value?.$d || value;
  if (!timeValue) return null;

  if (timeValue instanceof Date) {
    return timeValue.getHours() * 60 + timeValue.getMinutes();
  }

  if (typeof timeValue === "string") {
    const [hourMinute, meridiem] = timeValue.trim().split(" ");
    const [rawHour, rawMinute] = hourMinute.split(":");
    let hour = Number(rawHour);
    const minute = Number(rawMinute);

    if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
    if (meridiem === "PM" && hour !== 12) hour += 12;
    if (meridiem === "AM" && hour === 12) hour = 0;

    return hour * 60 + minute;
  }

  return null;
}

function getDateTimeValue(dateValue, timeValue) {
  const date = getDateOnlyValue(dateValue);
  const minutes = getTimeMinutesValue(timeValue);

  if (!date || minutes === null) return null;

  const dateTime = new Date(date);
  dateTime.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);

  return dateTime;
}

function formatDatePickerValue(value) {
  const dateValue = value?.$d || value;
  if (!dateValue) return "";

  if (dateValue instanceof Date) {
    return dateFormat(dateValue);
  }

  return dateValue;
}

function formatTimePickerValue(value) {
  const timeValue = value?.$d || value;
  if (!timeValue) return "";

  if (timeValue instanceof Date) {
    return timeFormat(timeValue);
  }

  return timeValue;
}

function SubRouteSortableItem({ subRoute, index, onDelete }) {
  const { ref, handleRef, isDragging } = useSortable({
    id: String(subRoute.sortableId),
    index,
    group: "booking-business-later-sub-routes",
  });

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minHeight: 44,
        px: 1,
        py: 0.75,
        border: "1px solid rgba(20, 83, 45, 0.16)",
        borderRadius: 1,
        bgcolor: isDragging ? "rgba(20, 83, 45, 0.08)" : "background.paper",
      }}
    >
      <IconButton ref={handleRef} size="small" sx={{ cursor: "grab" }}>
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {index + 1}. {subRoute.name}
      </Typography>
      <IconButton
        size="small"
        color="error"
        aria-label="eliminar paradero"
        onClick={() => onDelete(subRoute.sortableId)}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

const AddBookingBusinessLater = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [drivers, setDrivers] = useState(null);
  const [driver, setDriver] = useState(null);

  const [commissionOption, setCommissionOption] = useState(true);
  const [commissionValue, setCommissionValue] = useState(0);

  const [activePhone, setActivePhone] = useState(true);
  const handleActivePhone = (event) => setActivePhone(event.target.checked);
  const [otherPersonDialogOpen, setOtherPersonDialogOpen] = useState(false);
  const [endDateTimeDialogOpen, setEndDateTimeDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateDate, setDuplicateDate] = useState(null);
  const [duplicateTime, setDuplicateTime] = useState(null);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnDate, setReturnDate] = useState(null);
  const [returnTime, setReturnTime] = useState(null);
  const [otherPersonForm, setOtherPersonForm] = useState({
    otherPersonName: "",
    otherPersonPhone: "",
  });

  const handleOpenOtherPersonDialog = (values) => {
    setOtherPersonForm({
      otherPersonName: values.otherPersonName ?? "",
      otherPersonPhone: values.otherPersonPhone ?? "",
    });
    setOtherPersonDialogOpen(true);
  };

  const handleCloseOtherPersonDialog = () => {
    setOtherPersonDialogOpen(false);
  };

  const handleCloseEndDateTimeDialog = () => {
    setEndDateTimeDialogOpen(false);
  };

  const handleOpenDuplicateDialog = () => {
    setDuplicateDate(getDateOnlyValue(booking?.date));
    setDuplicateTime(getDateTimeValue(booking?.date, booking?.time));
    setDuplicateDialogOpen(true);
  };

  const handleCloseDuplicateDialog = () => {
    setDuplicateDialogOpen(false);
  };

  const handleOpenReturnDialog = () => {
    setReturnDate(getDateOnlyValue(booking?.date));
    setReturnTime(getDateTimeValue(booking?.date, booking?.time));
    setReturnDialogOpen(true);
  };

  const handleCloseReturnDialog = () => {
    setReturnDialogOpen(false);
  };

  const handleAcceptDuplicateDialog = async () => {
    if (!booking?.counterBooking) {
      snackBarDialog("No se encontro el pedido para duplicar", "error");
      return;
    }

    const selectedDate = dayjs(duplicateDate);
    const selectedTime = dayjs(duplicateTime);
    if (
      !duplicateDate ||
      !duplicateTime ||
      !selectedDate.isValid() ||
      !selectedTime.isValid()
    ) {
      snackBarDialog("Debe ingresar una fecha y hora validas", "error");
      return;
    }

    const date = selectedDate.format("DD/MM/YYYY");
    const time = selectedTime.format("hh:mm A");

    setIsLoading(true);
    try {
      const response = await DuplicateOrderBackup({
        id: booking.counterBooking,
        date,
        time,
      });
      const newCounterBooking = response?.id;
      const newUuid = response?.uuid;

      if (!newCounterBooking || !newUuid) {
        snackBarDialog("No se logro duplicar el Backup", "error");
        setIsLoading(false);
        return;
      }

      const duplicateBooking = createBookingCopy(booking, {
        uuid: newUuid,
        counterBooking: newCounterBooking,
        date,
        time,
      });

      await set(ref(db, `BookingBusiness/${newUuid}`), duplicateBooking);
      setDuplicateDialogOpen(false);
      snackBarDialog("PEDIDO DUPLICADO");
    } catch (error) {
      console.log(error);
      snackBarDialog("ERROR: No se pudo duplicar el Pedido", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptReturnDialog = async () => {
    if (!booking?.counterBooking) {
      snackBarDialog("No se encontro el pedido para el retorno", "error");
      return;
    }

    const selectedDate = dayjs(returnDate);
    const selectedTime = dayjs(returnTime);
    if (
      !returnDate ||
      !returnTime ||
      !selectedDate.isValid() ||
      !selectedTime.isValid()
    ) {
      snackBarDialog("Debe ingresar una fecha y hora validas", "error");
      return;
    }

    const date = selectedDate.format("DD/MM/YYYY");
    const time = selectedTime.format("hh:mm A");

    setIsLoading(true);
    try {
      const response = await CreateReturnOrderBackup({
        id: Number(booking.counterBooking),
        date,
        time,
      });
      const newCounterBooking = response?.data?.id;
      const newUuid = response?.data?.uuid;

      if (!newCounterBooking || !newUuid) {
        snackBarDialog("No se logro crear el Backup de retorno", "error");
        return;
      }

      const returnBooking = createBookingCopy(booking, {
        uuid: newUuid,
        counterBooking: newCounterBooking,
        date,
        time,
        isReturn: true,
      });

      await set(ref(db, `BookingBusiness/${newUuid}`), returnBooking);
      setReturnDialogOpen(false);
      snackBarDialog("PEDIDO DE RETORNO CREADO");
    } catch (error) {
      console.log(error);
      snackBarDialog("ERROR: No se pudo crear el Pedido de retorno", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtherPersonChange = (event) => {
    const { name, value } = event.target;
    setOtherPersonForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleAcceptOtherPerson = (setFieldValue) => {
    setFieldValue("otherPersonName", otherPersonForm.otherPersonName);
    setFieldValue("otherPersonPhone", otherPersonForm.otherPersonPhone);
    setOtherPersonDialogOpen(false);
  };

  const handleClearOtherPerson = (setFieldValue) => {
    setOtherPersonForm({
      otherPersonName: "",
      otherPersonPhone: "",
    });
    setFieldValue("otherPersonName", "");
    setFieldValue("otherPersonPhone", "");
  };

  const hasOtherPersonData = (values) => {
    return Boolean(
      String(values.otherPersonName ?? "").trim() ||
      String(values.otherPersonPhone ?? "").trim(),
    );
  };

  const getNoteAdminWithOtherPerson = (
    currentNoteAdmin,
    otherPersonName,
    otherPersonPhone,
  ) => {
    const noteAdminValue = String(currentNoteAdmin ?? "").trim();
    const otherPerson =
      `${otherPersonName ?? ""}-${otherPersonPhone ?? ""}`.trim();

    if (!otherPerson || otherPerson === "-") return noteAdminValue;
    if (noteAdminValue.includes(otherPerson)) return noteAdminValue;

    return noteAdminValue ? `${noteAdminValue} ${otherPerson}` : otherPerson;
  };

  // lista de direcciones
  const [addressApi, setAddressApi] = useState([]);
  const [addressOrigin, setAddressOrigin] = useState(null);
  const [addressDestination, setAddressDestination] = useState(null);
  const [subRoutesList, setSubRoutesList] = useState([]);
  const [openSubRoutesModal, setOpenSubRoutesModal] = useState(false);
  const [subRoutesModalList, setSubRoutesModalList] = useState([]);
  const [subRouteToAdd, setSubRouteToAdd] = useState(null);
  const [subRouteInput, setSubRouteInput] = useState("");
  const [destinationInputValue, setDestinationInputValue] = useState("");

  const [listPayments, setListPayments] = useState([]);
  const [noteAdmin, setNoteAdmin] = useState("");

  useEffect(() => {
    const starCountRef = ref(db, `BookingBusiness/${uuid}`);
    onValue(starCountRef, (snapshot) => {
      let data = snapshot.val();
      if (data === null) return console.log("No Booking");
      const origin = {
        id: uuidv4(),
        name: data.origin.name,
        latitude: data.origin.lat,
        longitude: data.origin.lng,
      };
      const destination = {
        id: uuidv4(),
        name: data.destination.name,
        latitude: data.destination.lat,
        longitude: data.destination.lng,
      };
      const orderedSubRoutesList = getOrderedSubRoutesList(data);
      data.subRoutesList = orderedSubRoutesList;
      setAddressOrigin(origin);
      setAddressDestination(destination);
      setSubRoutesList(orderedSubRoutesList);
      setDestinationInputValue(
        getDestinationValue(orderedSubRoutesList) || destination.name,
      );
      setBooking(data);
      setActivePhone(data.phoneActive !== undefined ? data.phoneActive : true);
      setNoteAdmin(
        getNoteAdminWithOtherPerson(
          data.noteAdmin,
          data.otherPersonName,
          data.otherPersonPhone,
        ),
      );
      const getPaymentsMysql = async () => {
        try {
          const resp = await getPaymentsList(data.counterBooking);
          setListPayments(resp.data.resp);
        } catch (error) {
          console.log(error);
        }
      };
      getPaymentsMysql();
    });
  }, []);

  const getPaymentsList = async (orderNumber) => {
    const resp = await getAdvanceCashOrder(orderNumber);
    return resp;
  };

  const [dateformat, setDateFormat] = useState("");
  const [timeFormatter, setTimeFormat] = useState("");
  const [dateEndFormat, setDateEndFormat] = useState("");
  const [timeEndFormatter, setTimeEndFormat] = useState("");

  useEffect(() => {
    if (booking) {
      if (booking.driver) {
        const driver = {
          name: booking.driver.name,
          lastname: booking.driver.lastname,
          brand: booking.driver.brand,
          color: booking.driver.color,
          driver_id: booking.driver.driver_id,
          model: booking.driver.model,
          bodywork: booking.driver.bodywork,
          plate: booking.driver.plate,
          vehicle_id: booking.driver.vehicle_id,
          percentageValue: booking.driver.percentageValue,
        };
        setCommissionValue(driver.percentageValue);
        setDriver(driver);
      }
      setDateFormat(dayjs(booking.date, "DD/MM/YYYY").toDate());
      setTimeFormat(formatterTimePicker(booking.date, booking.time));
      if (booking.booking_type == 3) {
        setDateEndFormat(dayjs(booking.dateEnd, "DD/MM/YYYY").toDate());
        setTimeEndFormat(formatterTimePicker(booking.dateEnd, booking.timeEnd));
      }
      setAdvance(booking.advance ? booking.advance : 0);
    }
  }, [booking]);

  useEffect(() => {
    const driversData = async () => {
      const verify = verifyExpireTokenRefresh();
      if (verify) return console.log("no token");

      const token = getTokenRefresh();
      try {
        const response = await getDriverAndVehicle(token);
        setDrivers(response?.data);
      } catch (error) {
        console.log(error);
      }
    };
    driversData();
  }, []);

  useEffect(() => {
    if (booking) {
      const getAddressEnterprise = async () => {
        try {
          const response = await getEnterpriseEmployeeAddress(
            "",
            booking.client,
            booking.enterpriseID,
          );
          setAddressApi([]);
          Object.values(response.data).map((address) => {
            const _address = {
              id: address.id,
              latitude: address.latitude,
              longitude: address.longitude,
              name: address.name,
              url: address.url,
            };
            setAddressApi((oldData) => [...oldData, _address]);
          });
        } catch (error) {
          console.log(error);
        }
      };
      getAddressEnterprise();
    }
  }, [booking]);

  const [isLoading, setIsLoading] = useState(false);
  const [stateSnk, setStateSnk] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [messageSnk, setMessageSnk] = useState("");
  const vertical = "top";
  const horizontal = "center";

  const handleSnkClose = () => setStateSnk(false);

  const snackBarDialog = (message, severity = "success") => {
    setMessageSnk(message);
    setSeverity(severity);
    setStateSnk(true);
  };

  const handleMouseDownPassword = (e) => e.preventDefault();

  const checkoutSchema = yup.object().shape({});

  const handleFormSubmit = async (values) => {
    setIsLoading(true);
    const submitSubRoutesList =
      subRoutesList.length > 0
        ? subRoutesList
        : addressDestination
          ? [normalizeSubRoute(addressDestination)]
          : [];
    const routeValues = getBookingRouteValues(submitSubRoutesList);
    const destinationRoute =
      routeValues.destination || normalizeSubRoute(addressDestination);
    values.driver = driver;
    values.statusService = driver ? "ASIGNADO" : "PENDIENTE";
    values.statusPayment = values.total > 0 ? "ASIGNADO" : "PENDIENTE";
    values.phoneActive = activePhone;
    values.advance = advance === "" ? 0 : Number(advance);
    values.noteAdmin = noteAdmin;
    values.subRoutes = routeValues.subRoutes;
    delete values.subRoutesList;
    try {
      values.origin = {
        name: addressOrigin.name,
        lat: addressOrigin.latitude,
        lng: addressOrigin.longitude,
        url: addressOrigin.url ? addressOrigin.url : "",
      };
      values.destination = {
        name: destinationRoute.name,
        lat: destinationRoute.lat,
        lng: destinationRoute.lng,
        url: destinationRoute.url ? destinationRoute.url : "",
      };

      values.date = formatDatePickerValue(values.date);
      values.time = formatTimePickerValue(values.time);
      if (values.booking_type == 3) {
        values.dateEnd = formatDatePickerValue(values.dateEnd);
        values.timeEnd = formatTimePickerValue(values.timeEnd);
      } else {
        values.dateEnd = "";
        values.timeEnd = "";
      }

      const openpay = {
        createBy: "A",
        status: "pending",
        btnPay: true,
        currency: "",
        transaction_id: "",
        order_id: "",
        operation_date: "",
        url: "",
      };
      values.openpay = openpay;

      if (driver) {
        const token = "asdf";
        if (commissionOption) {
          values.commission = values.driver.percentageValue;
        } else {
          values.commission = commissionValue;
        }
        if (!values.isCreated) {
          values.isCreated = true;
          values.statusBooking = "PAGADO";
          values.state_payment = "PENDIENTE";
          values.assigned = true;

          try {
            await createBookingBusiness(token, values);
            const dbRef = ref(db, `BookingBusiness/${values.uuid}`);

            if (values.uuid != "") await remove(dbRef);
            const notification = await sendBookingDriverNotificationUseCase(values);
            if (!notification.success) {
              snackBarDialog(notification.error, "warning");
            } else if (notification.data?.successCount > 0) {
              snackBarDialog("CONDUCTOR ASIGNADO Y NOTIFICACION ENVIADA");
            } else if (notification.data?.failureCount > 0) {
              snackBarDialog(
                "CONDUCTOR ASIGNADO, PERO NO SE PUDO ENVIAR LA NOTIFICACION",
                "warning",
              );
            } else {
              snackBarDialog(
                "CONDUCTOR ASIGNADO, PERO NO TIENE TOKEN PARA RECIBIR NOTIFICACIONES",
                "warning",
              );
            }
            setTimeout(() => {
              navigate("/admin/list-book-business-later");
            }, 5000);
          } catch (error) {
            snackBarDialog("NO SE LOGRO ASIGNAR CONDUCTOR", "error");
          }
        } else {
          await updateBookingBusiness(token, values);
          setTimeout(() => {
            navigate("/admin/list-book-business-later");
          }, 5000);
        }
        setIsLoading(false);
      } else {
        const urlRef = `BookingBusiness/${values.uuid}`;
        await update(ref(db, urlRef), values);

        const backup = {
          id: values.counterBooking,
          originName: addressOrigin.name,
          originLat: addressOrigin.latitude,
          originLng: addressOrigin.longitude,
          destinationName: destinationRoute.name,
          destinationLat: destinationRoute.lat,
          destinationLng: destinationRoute.lng,
          subRoutes: routeValues.subRoutes,

          date: values.date.$d ? dateFormat(values.date.$d) : values.date,
          time: values.time.$d ? timeFormat(values.time.$d) : values.time,
          dateEnd: values.booking_type == 3 ? values.dateEnd : "",
          timeEnd: values.booking_type == 3 ? values.timeEnd : "",
          bookingType: values.booking_type,

          persons: values.persons,
          advance: advance,
          tax: values.tax,
          toll: values.toll,
          price: values.price,
          total: values.total,
          discount: 0,
          note: values.note,
          noteAdmin: values.noteAdmin,
          currency: values.currency,
          paymentType: values.payment_type,
          otherPay: false,
          operationDate: "",
          otherPersonName: values.otherPersonName ?? "",
          otherPersonPhone: values.otherPersonPhone ?? "",
        };

        if (otherPay) {
          const currencyList = {
            1: "PEN",
            2: "USD",
          };

          const currDate = new Date();
          const datePay = `${dateFormat(currDate)} ${timeFormat(currDate)}`;

          const advReg = {
            orderNumber: values.counterBooking,
            transactionId: `${values.counterBooking}-${Date.now()}`,
            currency: currencyList[values.currency],
            amount: advance,
            operationDate: datePay,
            status: "completed",
            createdBy: "ADMIN",
          };
          const resp = await createAdvanceCash(advReg);
        }

        const resp = await UpdateOrderBackup(backup);

        if (resp.message === "success") {
          snackBarDialog("DATOS ACTUALIZADOS");
          setTimeout(() => {
            navigate("/admin/list-book-business-later");
          }, 2000);
        } else {
          snackBarDialog("ERROR: No se logro actualizar el Backup", "error");
        }

        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      snackBarDialog("Error: No Actualizado", "error");
    }
  };

  const [advance, setAdvance] = useState(0);
  const [otherPay, setOtherPay] = useState(false);

  const handleOtherPay = (e) => {
    const isChecked = e.target.checked;
    setOtherPay(isChecked);
  };

  const handleCloseSubRoutesModal = () => setOpenSubRoutesModal(false);

  const handleOpenSubRoutesModal = (routes) => {
    setSubRoutesModalList(getSortableSubRoutes(routes || []));
    setSubRouteToAdd(null);
    setSubRouteInput("");
    setOpenSubRoutesModal(true);
  };

  const handleSubRoutesDragEnd = (event) => {
    const operation = event.operation;

    if (!operation || operation.canceled || !isSortableOperation(operation)) {
      return;
    }

    const sourceIndex = operation.source.initialIndex;
    const targetIndex = operation.source.index;
    if (sourceIndex === targetIndex) return;

    setSubRoutesModalList((currentList) =>
      arrayMove(currentList, sourceIndex, targetIndex),
    );
  };

  const handleDeleteSubRoute = (sortableId) => {
    setSubRoutesModalList((currentList) =>
      currentList.filter((subRoute) => subRoute.sortableId !== sortableId),
    );
  };

  const handleAddSubRoute = (newValue) => {
    if (!newValue) {
      setSubRouteToAdd(null);
      return;
    }

    const id = getNextSubRouteId(subRoutesModalList);
    const newSubRoute = {
      id,
      name: newValue.name,
      lat: newValue.latitude,
      lng: newValue.longitude,
      url: newValue.url ?? "",
      sortableId: `booking-business-later-sub-route-${id}-${Date.now()}`,
    };

    setSubRoutesModalList((currentList) => [...currentList, newSubRoute]);
    setSubRouteToAdd(null);
    setSubRouteInput("");
  };

  const handleDestinationChange = (newValue, setFieldValue) => {
    setAddressDestination(newValue);
    setDestinationInputValue(newValue?.name || "");
    const nextSubRoutesList = newValue
      ? [
          {
            name: newValue.name,
            lat: newValue.latitude,
            lng: newValue.longitude,
            url: newValue.url ?? "",
          },
        ]
      : [];

    setSubRoutesList(nextSubRoutesList);
    setFieldValue("subRoutesList", nextSubRoutesList);
  };

  useEffect(() => {
    if (subRoutesList.length === 0) {
      setDestinationInputValue("");
      return;
    }

    setDestinationInputValue(getDestinationValue(subRoutesList));
  }, [subRoutesList]);

  return (
    <div>
      <CssBaseline />
      <div className="app">
        <main className="content">
          <Topbar />
          <Box m="5px">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Header
                title={`RESERVA: ${booking ? booking.enterpriseName : null}`}
                subtitle=""
              />
            </Box>
            <Box display="grid" gap="10px">
              <Box gridColumn="span 12" gridRow="span 1">
                {booking && (
                  <Formik
                    onSubmit={handleFormSubmit}
                    initialValues={booking}
                    validationSchema={checkoutSchema}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleBlur,
                      handleChange,
                      handleSubmit,
                      setFieldValue,
                    }) => {
                      const handleSaveSubRoutesModal = () => {
                        const nextSubRoutesList =
                          getSubRoutesFromModalList(subRoutesModalList);
                        const routeValues =
                          getBookingRouteValues(nextSubRoutesList);

                        if (!routeValues.destination) {
                          snackBarDialog(
                            "Debe seleccionar un destino",
                            "error",
                          );
                          return;
                        }

                        setSubRoutesList(nextSubRoutesList);
                        setFieldValue("subRoutesList", nextSubRoutesList);
                        setFieldValue("destination", routeValues.destination);
                        setFieldValue("subRoutes", routeValues.subRoutes);
                        setAddressDestination({
                          name: routeValues.destination.name,
                          latitude: routeValues.destination.lat,
                          longitude: routeValues.destination.lng,
                          url: routeValues.destination.url ?? "",
                        });
                        handleCloseSubRoutesModal();
                      };
                      const handleOpenEndDateTimeDialog = () => {
                        if (!dateEndFormat && values.dateEnd) {
                          setDateEndFormat(values.dateEnd);
                        } else if (!dateEndFormat && dateformat) {
                          setDateEndFormat(dateformat);
                          setFieldValue("dateEnd", dateformat);
                        }

                        if (!timeEndFormatter && values.timeEnd) {
                          setTimeEndFormat(values.timeEnd);
                        } else if (!timeEndFormatter && timeFormatter) {
                          setTimeEndFormat(timeFormatter);
                          setFieldValue("timeEnd", timeFormatter);
                        }

                        setEndDateTimeDialogOpen(true);
                      };
                      const handleAcceptEndDateTimeDialog = () => {
                        if (!dateEndFormat || !timeEndFormatter) {
                          snackBarDialog(
                            "Debe ingresar fecha y hora final",
                            "error",
                          );
                          return;
                        }

                        const startDate = getDateOnlyValue(
                          values.date || dateformat,
                        );
                        const endDate = getDateOnlyValue(dateEndFormat);
                        const startDateTime = getDateTimeValue(
                          values.date || dateformat,
                          values.time || timeFormatter,
                        );
                        const endDateTime = getDateTimeValue(
                          dateEndFormat,
                          timeEndFormatter,
                        );

                        if (!startDate || !endDate) {
                          snackBarDialog("Fecha final invalida", "error");
                          return;
                        }

                        if (endDate < startDate) {
                          snackBarDialog(
                            "La fecha final debe ser igual o superior a la fecha inicial",
                            "error",
                          );
                          return;
                        }

                        if (!startDateTime || !endDateTime) {
                          snackBarDialog("Hora final invalida", "error");
                          return;
                        }

                        if (endDateTime <= startDateTime) {
                          snackBarDialog(
                            "La hora final debe ser superior a la hora inicial",
                            "error",
                          );
                          return;
                        }

                        setFieldValue("booking_type", 3);
                        setFieldValue("dateEnd", dateEndFormat);
                        setFieldValue("timeEnd", timeEndFormatter);
                        setEndDateTimeDialogOpen(false);
                      };
                      const handleClearEndDateTimeDialog = () => {
                        const previousBookingType =
                          booking?.booking_type && booking.booking_type != 3
                            ? booking.booking_type
                            : 2;

                        setDateEndFormat("");
                        setTimeEndFormat("");
                        setFieldValue("booking_type", previousBookingType);
                        setFieldValue("dateEnd", "");
                        setFieldValue("timeEnd", "");
                        setEndDateTimeDialogOpen(false);
                      };

                      return (
                        <form onSubmit={handleSubmit}>
                          <Box backgroundColor={colors.primary[400]} p={1}>
                            <Grid container spacing={1} p={1}>
                              <Grid item xs={12}>
                                <TextField
                                  value={values.counterBooking}
                                  disabled
                                  type="text"
                                  label={"PEDIDO"}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                {addressApi ? (
                                  <Autocomplete
                                    value={addressOrigin}
                                    disablePortal
                                    name="client"
                                    id="combo-box-origin"
                                    onChange={(e, newValue) => {
                                      setAddressOrigin(newValue);
                                    }}
                                    options={addressApi}
                                    getOptionLabel={(option) =>
                                      `${option.name}`
                                    }
                                    renderInput={(params) => (
                                      <TextField {...params} label="Origen" />
                                    )}
                                  />
                                ) : (
                                  <TextField
                                    fullWidth
                                    disabled
                                    type="text"
                                    label={t("Lista de Direcciones")}
                                  />
                                )}
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                {addressApi ? (
                                  <Autocomplete
                                    value={addressDestination}
                                    inputValue={destinationInputValue}
                                    disablePortal
                                    name="client"
                                    id="combo-box-destination"
                                    open={
                                      subRoutesList.length > 1
                                        ? false
                                        : undefined
                                    }
                                    onChange={(e, newValue) => {
                                      handleDestinationChange(
                                        newValue,
                                        setFieldValue,
                                      );
                                    }}
                                    onInputChange={(
                                      event,
                                      newInputValue,
                                      reason,
                                    ) => {
                                      if (
                                        reason === "reset" ||
                                        subRoutesList.length > 1
                                      ) {
                                        return;
                                      }

                                      setDestinationInputValue(newInputValue);
                                    }}
                                    options={addressApi}
                                    getOptionLabel={(option) =>
                                      `${option.name}`
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                      option?.id === value?.id ||
                                      (option?.name === value?.name &&
                                        option?.latitude === value?.latitude &&
                                        option?.longitude === value?.longitude)
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label="Destino"
                                        InputProps={{
                                          ...params.InputProps,
                                          endAdornment: (
                                            <InputAdornment position="end">
                                              <IconButton
                                                edge="end"
                                                aria-label="editar paraderos"
                                                onMouseDown={(event) => {
                                                  event.preventDefault();
                                                }}
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                  handleOpenSubRoutesModal(
                                                    subRoutesList.length > 0
                                                      ? subRoutesList
                                                      : addressDestination
                                                        ? [
                                                            normalizeSubRoute(
                                                              addressDestination,
                                                            ),
                                                          ]
                                                        : [],
                                                  );
                                                }}
                                              >
                                                <LocationOnIcon />
                                              </IconButton>
                                              {params.InputProps.endAdornment}
                                            </InputAdornment>
                                          ),
                                        }}
                                        inputProps={{
                                          ...params.inputProps,
                                          readOnly: subRoutesList.length > 1,
                                        }}
                                        onClick={() => {
                                          if (subRoutesList.length > 1) {
                                            handleOpenSubRoutesModal(
                                              subRoutesList,
                                            );
                                          }
                                        }}
                                        sx={{
                                          cursor:
                                            subRoutesList.length > 1
                                              ? "pointer"
                                              : "text",
                                          "& .MuiInputBase-input": {
                                            cursor:
                                              subRoutesList.length > 1
                                                ? "pointer"
                                                : "text",
                                          },
                                        }}
                                      />
                                    )}
                                  />
                                ) : (
                                  <TextField
                                    fullWidth
                                    disabled
                                    type="text"
                                    label={t("Lista de Direcciones")}
                                  />
                                )}
                              </Grid>
                              <Grid item container xs={12} md={6} spacing={0.5}>
                                <Grid
                                  item
                                  xs={12}
                                  sm={hasOtherPersonData(values) ? 6 : 12}
                                >
                                  <TextField
                                    value={values.clientName}
                                    fullWidth
                                    variant="outlined"
                                    color="success"
                                    type="text"
                                    label={t("client")}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <IconButton
                                            aria-label="datos de otra persona"
                                            edge="end"
                                            onClick={() =>
                                              handleOpenOtherPersonDialog(
                                                values,
                                              )
                                            }
                                            style={{ marginRight: 4 }}
                                          >
                                            <PersonIcon />
                                          </IconButton>
                                          <Checkbox
                                            style={{ marginRight: -15 }}
                                            checked={activePhone}
                                            onChange={handleActivePhone}
                                            color="success"
                                          />
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Grid>
                                {hasOtherPersonData(values) && (
                                  <>
                                    <Grid item xs={6} sm={3}>
                                      <TextField
                                        value={values.otherPersonName ?? ""}
                                        fullWidth
                                        variant="outlined"
                                        color="success"
                                        type="text"
                                        label="PARA:"
                                        name="otherPersonName"
                                        InputProps={{ readOnly: true }}
                                        inputProps={{
                                          style: { textTransform: "uppercase" },
                                        }}
                                      />
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                      <TextField
                                        value={values.otherPersonPhone ?? ""}
                                        fullWidth
                                        variant="outlined"
                                        color="success"
                                        type="tel"
                                        label="Tel:"
                                        name="otherPersonPhone"
                                        InputProps={{ readOnly: true }}
                                      />
                                    </Grid>
                                  </>
                                )}
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <Grid container spacing={1}>
                                  <Grid item xs={6} sm={3} md={3}>
                                    <LocalizationProvider
                                      dateAdapter={AdapterDayjs}
                                    >
                                      <Stack spacing={2}>
                                        <MobileDatePicker
                                          label={t("date")}
                                          inputFormat="DD/MM/YYYY"
                                          name="date"
                                          value={dateformat}
                                          onChange={(newValue) => {
                                            setDateFormat(newValue);
                                            values.date = newValue;
                                          }}
                                          renderInput={(params) => (
                                            <TextField {...params} />
                                          )}
                                        />
                                      </Stack>
                                    </LocalizationProvider>
                                  </Grid>
                                  <Grid item xs={6} sm={3} md={3}>
                                    <LocalizationProvider
                                      dateAdapter={AdapterDayjs}
                                    >
                                      <Stack spacing={3}>
                                        <MobileTimePicker
                                          label={t("time")}
                                          value={timeFormatter}
                                          name="time"
                                          onChange={(newValue) => {
                                            setTimeFormat(newValue);
                                            values.time = newValue;
                                          }}
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                  <InputAdornment position="end">
                                                    <IconButton
                                                      edge="end"
                                                      aria-label="fecha y hora final"
                                                      onMouseDown={(event) => {
                                                        event.preventDefault();
                                                      }}
                                                      onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleOpenEndDateTimeDialog();
                                                      }}
                                                    >
                                                      <AccessTimeIcon fontSize="small" />
                                                    </IconButton>
                                                    {
                                                      params.InputProps
                                                        .endAdornment
                                                    }
                                                  </InputAdornment>
                                                ),
                                              }}
                                            />
                                          )}
                                        />
                                      </Stack>
                                    </LocalizationProvider>
                                  </Grid>

                                  <Grid item xs={6} sm={3} md={3}>
                                    <TextField
                                      value={values.persons}
                                      fullWidth
                                      variant="outlined"
                                      color="success"
                                      type="number"
                                      label={t("persons")}
                                      onBlur={handleBlur}
                                      onChange={handleChange}
                                      name="persons"
                                    />
                                  </Grid>
                                  <Grid item xs={6} sm={3} md={3}>
                                    <FormControl fullWidth>
                                      <InputLabel>{t("Model")}</InputLabel>
                                      <Select
                                        value={values.vehicle}
                                        id="vehicle"
                                        name="vehicle"
                                        label={t("vehicle")}
                                        onChange={handleChange}
                                      >
                                        <MenuItem value={1}>
                                          {t("SEDAN")}
                                        </MenuItem>
                                        <MenuItem value={2}>
                                          {t("SUV")}
                                        </MenuItem>
                                        <MenuItem value={3}>
                                          {t("MINIVAN")}
                                        </MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                </Grid>
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <TextField
                                  value={values.note}
                                  fullWidth
                                  variant="outlined"
                                  color="success"
                                  type="text"
                                  label={t("notes")}
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  name="note"
                                />
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                {drivers && (
                                  <Autocomplete
                                    value={driver}
                                    disablePortal
                                    id="combo-box-driver"
                                    onChange={(e, newValue) => {
                                      setDriver(newValue);
                                    }}
                                    options={drivers}
                                    getOptionLabel={(option) =>
                                      `${option.name} ${option.lastname} ${option.brand} ${option.model} ${option.bodywork} ${option.plate} ${option.color}`
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label={t("driver")}
                                      />
                                    )}
                                  />
                                )}
                              </Grid>

                              <Grid item xs={12} sm={3} md={3}>
                                <Grid container spacing={1}>
                                  <Grid item xs={6} sm={12} md={12}>
                                    <FormControl fullWidth>
                                      <InputLabel>{t("Tipo Pago")}</InputLabel>
                                      <Select
                                        value={values.payment_type}
                                        id="payment_type"
                                        name="payment_type"
                                        label={t("payment")}
                                        onChange={handleChange}
                                      >
                                        <MenuItem value={1}>
                                          {t("EFECTIVO")}
                                        </MenuItem>
                                        <MenuItem value={2}>
                                          {t("CREDITO")}
                                        </MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={6} sm={12} md={12}>
                                    <FormControl fullWidth variant="outlined">
                                      <InputLabel htmlFor="outlined-adornment-password">
                                        COMISION
                                      </InputLabel>
                                      <OutlinedInput
                                        value={commissionValue}
                                        name="commissionValue"
                                        onChange={(e) => {
                                          setCommissionValue(e.target.value);
                                        }}
                                        disabled={commissionOption}
                                        id="control-dni"
                                        endAdornment={
                                          <InputAdornment position="end">
                                            <IconButton
                                              aria-label="toggle password visibility"
                                              onMouseDown={
                                                handleMouseDownPassword
                                              }
                                              edge="end"
                                              onClick={(e) => {
                                                setCommissionOption(
                                                  !commissionOption,
                                                );
                                              }}
                                            >
                                              <EditIcon />
                                            </IconButton>
                                          </InputAdornment>
                                        }
                                        label="COMISION"
                                        aria-describedby="outlined-helper-text"
                                      />
                                    </FormControl>
                                  </Grid>
                                </Grid>
                              </Grid>

                              <Grid item xs={12} sm={3} md={3}>
                                <Grid container spacing={1}>
                                  <Grid item xs={6} sm={12} md={12}>
                                    <FormControl fullWidth>
                                      <InputLabel>{t("currency")}</InputLabel>
                                      <Select
                                        value={values.currency}
                                        id="currency"
                                        name="currency"
                                        label={t("currency")}
                                        onChange={handleChange}
                                      >
                                        <MenuItem value={1}>
                                          {t("PEN")}
                                        </MenuItem>
                                        <MenuItem value={2}>
                                          {t("USD")}
                                        </MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  {values.booking_type == 3 && (
                                    <Grid item xs={6} sm={12}>
                                      <Grid container spacing={1}>
                                        <Grid item xs={6} sm={6} md={6}>
                                          <LocalizationProvider
                                            dateAdapter={AdapterDayjs}
                                          >
                                            <Stack spacing={2}>
                                              <MobileDatePicker
                                                label={t("dateEnd")}
                                                inputFormat="DD/MM/YYYY"
                                                name="dateEnd"
                                                value={dateEndFormat}
                                                onChange={(newValue) => {
                                                  setDateEndFormat(newValue);
                                                  values.dateEnd = newValue;
                                                }}
                                                renderInput={(params) => (
                                                  <TextField {...params} />
                                                )}
                                              />
                                            </Stack>
                                          </LocalizationProvider>
                                        </Grid>
                                        <Grid item xs={6} sm={6} md={6}>
                                          <LocalizationProvider
                                            dateAdapter={AdapterDayjs}
                                          >
                                            <Stack spacing={3}>
                                              <MobileTimePicker
                                                label={t("timeEnd")}
                                                value={timeEndFormatter}
                                                name="timeEnd"
                                                onChange={(newValue) => {
                                                  setTimeEndFormat(newValue);
                                                  values.timeEnd = newValue;
                                                }}
                                                renderInput={(params) => (
                                                  <TextField {...params} />
                                                )}
                                              />
                                            </Stack>
                                          </LocalizationProvider>
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                  )}
                                </Grid>
                              </Grid>

                              <Grid item xs={12} sm={3} md={3}>
                                <Grid container spacing={1}>
                                  <Grid item xs={6} sm={12} md={12}>
                                    <TextField
                                      value={values.tax}
                                      fullWidth
                                      variant="outlined"
                                      color="success"
                                      type="number"
                                      label={t("IMPUESTO")}
                                      onChange={handleChange}
                                      name="tax"
                                    />
                                  </Grid>
                                  <Grid item xs={6} sm={12} md={12}>
                                    <TextField
                                      value={values.toll}
                                      fullWidth
                                      variant="outlined"
                                      color="success"
                                      type="number"
                                      label={t("PEAJE")}
                                      onChange={handleChange}
                                      name="toll"
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>

                              <Grid item xs={12} sm={3} md={3}>
                                <Grid container spacing={1}>
                                  <Grid item xs={6} sm={12} md={12}>
                                    <TextField
                                      value={values.price}
                                      fullWidth
                                      variant="outlined"
                                      color="success"
                                      type="number"
                                      label={t("P CONDUCTOR")}
                                      onChange={handleChange}
                                      name="price"
                                    />
                                  </Grid>
                                  <Grid item xs={6} sm={12} md={12}>
                                    <TextField
                                      value={values.total}
                                      fullWidth
                                      variant="outlined"
                                      color="success"
                                      type="number"
                                      label={t("TOTAL")}
                                      onChange={handleChange}
                                      name="total"
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>

                              <Grid item xs={12} sm={12} md={12}>
                                <TextField
                                  value={noteAdmin}
                                  fullWidth
                                  variant="outlined"
                                  color="success"
                                  type="text"
                                  label="Notas del Admin"
                                  onChange={(e) => setNoteAdmin(e.target.value)}
                                  name="noteAdmin"
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          aria-label="booking return"
                                          edge="end"
                                          disabled={isLoading}
                                          onClick={handleOpenReturnDialog}
                                          style={{ marginRight: 4 }}
                                        >
                                          <SwapHorizIcon />
                                        </IconButton>
                                        <IconButton
                                          aria-label="booking duplicate"
                                          edge="end"
                                          disabled={isLoading}
                                          onClick={handleOpenDuplicateDialog}
                                          style={{ marginRight: 4 }}
                                        >
                                          <FileCopyIcon />
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12} sm={12} md={12}>
                                <div>
                                  {listPayments.map((pay, idx) => {
                                    return (
                                      <Fragment key={idx}>
                                        <span style={{ fontWeight: "bold" }}>
                                          {pay.created_by}
                                        </span>
                                        {", "}
                                        <span style={{ fontWeight: "bold" }}>
                                          Pago:{" "}
                                        </span>
                                        {pay.amount} {pay.currency},{" "}
                                        <span style={{ fontWeight: "bold" }}>
                                          Fecha:{" "}
                                        </span>
                                        {pay.operation_date}
                                        {". "}
                                      </Fragment>
                                    );
                                  })}
                                </div>
                              </Grid>

                              <Grid item xs={6} sm={3} md={3}>
                                <TextField
                                  value={advance}
                                  name="advance"
                                  fullWidth
                                  variant="outlined"
                                  color="success"
                                  type="number"
                                  label="ADELANTO"
                                  onChange={(e) => setAdvance(e.target.value)}
                                  InputProps={{
                                    endAdornment: (
                                      <Checkbox
                                        style={{ marginRight: -15 }}
                                        checked={otherPay}
                                        onChange={handleOtherPay}
                                        color="success"
                                      />
                                    ),
                                  }}
                                />
                              </Grid>

                              <Grid item xs={0} sm={6} md={6}></Grid>

                              <Grid item xs={12} sm={3} md={3}>
                                <Button
                                  fullWidth
                                  type="submit"
                                  color="success"
                                  variant="contained"
                                  sx={{
                                    height: 50,
                                    color: "white",
                                    fontWeight: "bold",
                                  }}
                                  disabled={isLoading}
                                >
                                  {"GUARDAR"}
                                </Button>
                              </Grid>
                            </Grid>
                            <SnackBar
                              stateSnk={stateSnk}
                              vertical={vertical}
                              horizontal={horizontal}
                              duration={4000}
                              handleClose={handleSnkClose}
                              severity={severity}
                              messageSnk={messageSnk}
                            />
                          </Box>
                          <Dialog
                            open={duplicateDialogOpen}
                            onClose={
                              isLoading ? undefined : handleCloseDuplicateDialog
                            }
                            fullWidth
                            maxWidth="xs"
                          >
                            <DialogTitle>
                              Deseas Duplicar este Pedido?
                            </DialogTitle>
                            <DialogContent>
                              <Stack spacing={2} sx={{ pt: 1 }}>
                                <Typography variant="body2">
                                  Confirma o cambia la fecha y hora del nuevo pedido.
                                </Typography>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                  <MobileDatePicker
                                    label={t("date")}
                                    inputFormat="DD/MM/YYYY"
                                    value={duplicateDate}
                                    onChange={setDuplicateDate}
                                    renderInput={(params) => (
                                      <TextField {...params} fullWidth />
                                    )}
                                  />
                                  <MobileTimePicker
                                    label={t("time")}
                                    value={duplicateTime}
                                    onChange={setDuplicateTime}
                                    renderInput={(params) => (
                                      <TextField {...params} fullWidth />
                                    )}
                                  />
                                </LocalizationProvider>
                              </Stack>
                            </DialogContent>
                            <DialogActions>
                              <Button
                                type="button"
                                variant="contained"
                                color="error"
                                onClick={handleCloseDuplicateDialog}
                                disabled={isLoading}
                              >
                                No
                              </Button>
                              <Button
                                type="button"
                                variant="contained"
                                color="success"
                                onClick={handleAcceptDuplicateDialog}
                                disabled={isLoading}
                              >
                                Si, Duplicar Pedido
                              </Button>
                            </DialogActions>
                          </Dialog>
                          <Dialog
                            open={returnDialogOpen}
                            onClose={
                              isLoading ? undefined : handleCloseReturnDialog
                            }
                            fullWidth
                            maxWidth="xs"
                          >
                            <DialogTitle>
                              ¿Deseas crear el pedido de retorno?
                            </DialogTitle>
                            <DialogContent>
                              <Stack spacing={2} sx={{ pt: 1 }}>
                                <Typography variant="body2">
                                  Confirma o cambia la fecha y hora del retorno.
                                </Typography>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                  <MobileDatePicker
                                    label={t("date")}
                                    inputFormat="DD/MM/YYYY"
                                    value={returnDate}
                                    onChange={setReturnDate}
                                    renderInput={(params) => (
                                      <TextField {...params} fullWidth />
                                    )}
                                  />
                                  <MobileTimePicker
                                    label={t("time")}
                                    value={returnTime}
                                    onChange={setReturnTime}
                                    renderInput={(params) => (
                                      <TextField {...params} fullWidth />
                                    )}
                                  />
                                </LocalizationProvider>
                              </Stack>
                            </DialogContent>
                            <DialogActions>
                              <Button
                                type="button"
                                variant="contained"
                                color="error"
                                onClick={handleCloseReturnDialog}
                                disabled={isLoading}
                              >
                                No
                              </Button>
                              <Button
                                type="button"
                                variant="contained"
                                color="success"
                                onClick={handleAcceptReturnDialog}
                                disabled={isLoading}
                              >
                                Sí, Crear Retorno
                              </Button>
                            </DialogActions>
                          </Dialog>
                          <Dialog
                            open={otherPersonDialogOpen}
                            onClose={handleCloseOtherPersonDialog}
                            fullWidth
                            maxWidth="sm"
                          >
                            <DialogTitle>Datos de otra persona</DialogTitle>
                            <DialogContent>
                              <Stack spacing={2} sx={{ pt: 1 }}>
                                <TextField
                                  value={otherPersonForm.otherPersonName}
                                  fullWidth
                                  variant="outlined"
                                  color="success"
                                  type="text"
                                  label="Nombre"
                                  name="otherPersonName"
                                  onChange={handleOtherPersonChange}
                                  inputProps={{
                                    style: { textTransform: "uppercase" },
                                  }}
                                />
                                <TextField
                                  value={otherPersonForm.otherPersonPhone}
                                  fullWidth
                                  variant="outlined"
                                  color="success"
                                  type="tel"
                                  label="Telefono"
                                  name="otherPersonPhone"
                                  onChange={handleOtherPersonChange}
                                />
                              </Stack>
                            </DialogContent>
                            <DialogActions>
                              <Button
                                type="button"
                                variant="contained"
                                color="error"
                                onClick={handleCloseOtherPersonDialog}
                              >
                                Cancelar
                              </Button>
                              <Button
                                type="button"
                                variant="contained"
                                color="warning"
                                onClick={() =>
                                  handleClearOtherPerson(setFieldValue)
                                }
                              >
                                Limpiar
                              </Button>
                              <Button
                                type="button"
                                variant="contained"
                                color="secondary"
                                onClick={() =>
                                  handleAcceptOtherPerson(setFieldValue)
                                }
                              >
                                Aceptar
                              </Button>
                            </DialogActions>
                          </Dialog>
                          <EndDateTimeDialog
                            open={endDateTimeDialogOpen}
                            onClose={handleCloseEndDateTimeDialog}
                            dateEnd={dateEndFormat}
                            timeEnd={timeEndFormatter}
                            dateLabel={t("dateEnd")}
                            timeLabel={t("timeEnd")}
                            onDateEndChange={(newValue) => {
                              setDateEndFormat(newValue);
                              setFieldValue("dateEnd", newValue);
                            }}
                            onTimeEndChange={(newValue) => {
                              setTimeEndFormat(newValue);
                              setFieldValue("timeEnd", newValue);
                            }}
                            onClear={handleClearEndDateTimeDialog}
                            onAccept={handleAcceptEndDateTimeDialog}
                          />
                          <Dialog
                            open={openSubRoutesModal}
                            onClose={handleSaveSubRoutesModal}
                            fullWidth
                            maxWidth="xs"
                            PaperProps={{
                              sx: {
                                height: "80vh",
                                maxHeight: "90vh",
                              },
                            }}
                          >
                            <DialogTitle>
                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1.5}
                                sx={{
                                  alignItems: { xs: "stretch", sm: "center" },
                                }}
                              >
                                <Typography variant="h6" sx={{ flexShrink: 0 }}>
                                  Paraderos
                                </Typography>
                                <Autocomplete
                                  value={subRouteToAdd}
                                  inputValue={subRouteInput}
                                  onChange={(event, newValue) => {
                                    handleAddSubRoute(newValue);
                                  }}
                                  onInputChange={(
                                    event,
                                    newInputValue,
                                    reason,
                                  ) => {
                                    if (reason === "reset") return;
                                    setSubRouteInput(newInputValue);
                                  }}
                                  options={addressApi || []}
                                  getOptionLabel={(option) =>
                                    option?.name ? `${option.name}` : ""
                                  }
                                  isOptionEqualToValue={(option, value) =>
                                    option?.id === value?.id ||
                                    (option?.name === value?.name &&
                                      option?.latitude === value?.latitude &&
                                      option?.longitude === value?.longitude)
                                  }
                                  sx={{ flex: 1 }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label={t("Destino")}
                                      size="small"
                                    />
                                  )}
                                />
                              </Stack>
                            </DialogTitle>
                            <DialogContent>
                              <Stack spacing={1} sx={{ pt: 1 }}>
                                <DragDropProvider
                                  onDragEnd={handleSubRoutesDragEnd}
                                >
                                  <Stack spacing={1}>
                                    {subRoutesModalList.map(
                                      (subRoute, index) => (
                                        <SubRouteSortableItem
                                          key={subRoute.sortableId}
                                          subRoute={subRoute}
                                          index={index}
                                          onDelete={handleDeleteSubRoute}
                                        />
                                      ),
                                    )}
                                  </Stack>
                                </DragDropProvider>
                              </Stack>
                            </DialogContent>
                            <DialogActions>
                              <Button
                                type="button"
                                variant="contained"
                                color="error"
                                onClick={handleSaveSubRoutesModal}
                              >
                                {t("Cerrar")}
                              </Button>
                            </DialogActions>
                          </Dialog>
                        </form>
                      );
                    }}
                  </Formik>
                )}
              </Box>
            </Box>
          </Box>
        </main>
      </div>
    </div>
  );
};

export default AddBookingBusinessLater;
